import type { Page } from '@inertiajs/core'
import { createInertiaApp as createInertiaAppOriginal, router } from '@inertiajs/vue3'
import { deepCamelizeKeys } from '@shared/object'
export { router }
export type { Page }

export type CreateInertiaAppProps = Parameters<typeof createInertiaAppOriginal>[0]

// Inertia v2+ stores the initial page in a <script data-page> element.
// Read it ourselves so we can camelize props before the router consumes them.
function camelizedInitialPage (id: string): Page | undefined {
  if (typeof document === 'undefined') return undefined

  const scriptEl = document.querySelector(`script[data-page="${id}"][type="application/json"]`)
  if (!scriptEl?.textContent) return undefined

  const page = JSON.parse(scriptEl.textContent) as Page
  page.props = deepCamelizeKeys(page.props)
  currentPage = page
  return page
}

// Tracks the latest page so non-component code (tracking, api headers, etc.) can
// read shared props without going through Vue's `usePage()`.
let currentPage: Page | undefined

// Patch `router.init` so that every `swapComponent` callback receives a page
// whose props have been camelized. swapComponent is the single chokepoint every
// page transition flows through (initial mount, visits, deferred-prop responses,
// back/forward), so this guarantees Vue components always see camelCase keys
// regardless of which code path produced the page.
const originalRouterInit = (router as any).init.bind(router)
;(router as any).init = function (params: any) {
  const originalSwap = params.swapComponent
  params.swapComponent = (options: any) => {
    if (options?.page?.props) options.page.props = deepCamelizeKeys(options.page.props)
    return originalSwap(options)
  }
  return originalRouterInit(params)
}

if (typeof document !== 'undefined') {
  // Belt-and-suspenders: also camelize at `beforeUpdate`, which fires before
  // the page is persisted to history state, so back/forward restores see
  // camelCase too.
  //
  // We also update `currentPage` here (not just on `inertia:navigate`) because
  // Inertia v3's `setPage` forces `replace = true` whenever the new URL equals
  // the current URL, and `fireNavigateEvent` is gated on `!replace`. The
  // corporate `/corp/switch_account/:id` route `redirect_back`s to the same
  // URL the user came from, which hits exactly that path — without this
  // listener `currentPage` stays pinned to the previous organization and the
  // next request sends a stale `X-Organization-Id`, snapping the user back to
  // the account they just switched away from.
  document.addEventListener('inertia:beforeUpdate', (event: any) => {
    const page = event.detail?.page
    if (page?.props) page.props = deepCamelizeKeys(page.props)
    if (page) currentPage = page
  })

  // Keep `currentPage` in sync after each navigation (initial load, visits,
  // and back/forward).
  document.addEventListener('inertia:navigate', (event: any) => {
    if (event.detail?.page) currentPage = event.detail.page
  })

  // Attach `X-Inertia-Layout` (and `X-Organization-Id`) to every Inertia
  // visit. Rails' `stale_inertia_request?` (config/initializers/inertia.rb)
  // uses the layout header to detect cross-app navigations and force a hard
  // reload via 409 + X-Inertia-Location; without it, the server returns an
  // Inertia 200 for the destination app and the current bundle blows up
  // trying to resolve a page it doesn't ship. Pre-v3 this was handled by an
  // axios request interceptor (`corporate/services/api.ts`), but v3's xhr
  // client doesn't fire axios interceptors.
  // NOTE: Inertia's `onGlobalEvent` invokes the callback with the raw
  // `CustomEvent`, not with the visit, even though `GlobalEventCallback`
  // types it as `(visit) => …`. Pull the visit off `event.detail`.
  router.on('before', (event) => {
    const visit = event.detail?.visit
    if (!visit?.headers) return
    const props = currentPage?.props as { layout?: string, organization?: { id?: string } } | undefined
    if (props?.layout) visit.headers['X-Inertia-Layout'] = props.layout
    if (props?.organization?.id) visit.headers['X-Organization-Id'] = props.organization.id
  })

  // Handle Giftly's `AjaxRedirect` (HTTP 279) from Inertia visits. Inertia v3
  // uses its own xhr-based http client (not axios), so `handleAjaxRedirects`
  // in `@shared/axios` doesn't see Inertia's responses. Without this, a
  // server-side redirect from a POST (e.g. /corp/login → /corp/onboarding)
  // would surface as an unhandled `httpException` and the navigation would
  // never happen. Cancelling the event suppresses the default
  // `dialog.show(response.data)` behavior; we then drive the redirect via
  // a hard navigation. We use `location.href` rather than `router.get`
  // because the redirect target often crosses Inertia apps (e.g. login is
  // in `corporate_public`, onboarding is in `corporate`) and an Inertia
  // visit would fail to resolve the new app's page component.
  document.addEventListener('inertia:httpException', (event: any) => {
    const response = event.detail?.response
    if (response?.status === 279 && response.headers?.location) {
      event.preventDefault()
      location.href = response.headers.location
    }
  })
}

export function createInertiaApp (options: CreateInertiaAppProps): ReturnType<typeof createInertiaAppOriginal> {
  const id = (options as { id?: string })?.id ?? 'app'
  const initialPage = camelizedInitialPage(id)
  return createInertiaAppOriginal({
    ...(initialPage ? { page: initialPage } : {}),
    ...options,
  })
}

// Public: Returns the current Inertia page (for use outside Vue components).
export function getCurrentPage (): Page | undefined {
  return currentPage
}

// Public: Returns the referral_spec for the current session.
export function getReferralSpec () {
  return currentPage?.props?.referralSpec
}
